// Subsequence + word-boundary scorer for the command palette.
//
// Returns null when the query letters cannot be found in `target` in order,
// otherwise a number where larger = better. Stable enough for sort(), not
// claiming to be VS-Code grade.

const WORD_BOUNDARY = /[\s._:/-]/;

export function score(query: string, target: string): number | null {
    if (!query) return 0; // empty query → all entries pass; sort by other criteria
    const q = query.toLowerCase();
    const t = target.toLowerCase();
    if (q.length > t.length) return null;

    let s = 0;
    let ti = 0;
    let lastMatch = -1;
    let consecutive = 0;
    for (let qi = 0; qi < q.length; qi++) {
        const ch = q[qi]!;
        let found = -1;
        for (; ti < t.length; ti++) {
            if (t[ti] === ch) { found = ti; ti++; break; }
        }
        if (found === -1) return null;

        // Base hit
        s += 1;
        // Prefix bonus
        if (found === 0) s += 5;
        // Word-boundary bonus
        else if (WORD_BOUNDARY.test(t[found - 1] ?? '')) s += 3;
        // Camel-bump bonus (lowercase before, uppercase target char in original)
        else if (target[found] && target[found] !== target[found]!.toLowerCase()) s += 1;
        // Consecutive-letter bonus
        if (lastMatch === found - 1) { consecutive += 1; s += consecutive * 2; }
        else { consecutive = 0; }
        lastMatch = found;
    }

    // Tighter matches (smaller span between first and last hit) win ties.
    s -= (lastMatch - (q.length - 1)) * 0.1;
    return s;
}

/** Score a query against (label, aliases, id), keeping the best. */
export function scoreCommand(
    query: string,
    fields: { label: string; aliases?: ReadonlyArray<string>; id: string },
): number | null {
    if (!query) return 0;
    let best: number | null = null;
    const tryField = (target: string, weight: number): void => {
        const s = score(query, target);
        if (s === null) return;
        const adjusted = s * weight;
        if (best === null || adjusted > best) best = adjusted;
    };
    tryField(fields.label, 1.0);
    if (fields.aliases) for (const a of fields.aliases) tryField(a, 0.85);
    tryField(fields.id, 0.65);
    return best;
}
